import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import TreeLogo from '@/components/TreeLogo';

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(45 25% 97%)' }}>
      {/* Header */}
      <nav className="sticky top-0 z-50" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 35% 88%)' }}>
              <TreeLogo size={24} className="text-slate-600" />
            </div>
            <h1 className="text-lg text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
              Rooted
            </h1>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            className="rounded-full text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            ← Back
          </Button>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-4xl mb-8" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
          Terms and Conditions
        </h1>
        
        <div className="prose prose-slate max-w-none" style={{ color: 'hsl(220 12% 40%)' }}>
          <p className="text-sm mb-8" style={{ color: 'hsl(220 12% 55%)' }}>
            Last updated: January 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              1. Acceptance of Terms
            </h2>
            <p className="mb-4 leading-relaxed">
              By accessing or using Rooted ("the Service"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              2. Description of Service
            </h2>
            <p className="mb-4 leading-relaxed">
              Rooted is a digital devotional application that provides daily scripture readings, reflections, writing prompts, and prayers following the liturgical calendar. The Service includes:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Access to daily devotional content</li>
              <li>Personal journaling features</li>
              <li>Audio playback of devotionals</li>
              <li>Bookmarking and reading streak tracking</li>
              <li>Calendar navigation for past devotionals</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              3. Subscription and Payment
            </h2>
            <h3 className="text-lg font-medium mb-2" style={{ color: 'hsl(250 15% 40%)' }}>3.1 Subscription Plans</h3>
            <p className="mb-4 leading-relaxed">
              We offer the following subscription options:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Annual Subscription:</strong> $35.99 per year</li>
              <li><strong>Monthly Subscription:</strong> $4.99 per month</li>
            </ul>

            <h3 className="text-lg font-medium mb-2" style={{ color: 'hsl(250 15% 40%)' }}>3.2 Billing</h3>
            <p className="mb-4 leading-relaxed">
              Your subscription will automatically renew at the end of each billing period unless you cancel before the renewal date. Payment is processed securely through Stripe.
            </p>

            <h3 className="text-lg font-medium mb-2" style={{ color: 'hsl(250 15% 40%)' }}>3.3 Cancellation</h3>
            <p className="mb-4 leading-relaxed">
              You may cancel your subscription at any time through your account settings. Upon cancellation, you will retain access to the Service until the end of your current billing period. No refunds will be provided for partial billing periods.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              4. User Accounts
            </h2>
            <p className="mb-4 leading-relaxed">
              To use the Service, you must create an account. You are responsible for:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Providing accurate and complete information</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              5. Content and Intellectual Property
            </h2>
            <h3 className="text-lg font-medium mb-2" style={{ color: 'hsl(250 15% 40%)' }}>5.1 Our Content</h3>
            <p className="mb-4 leading-relaxed">
              All devotional content, including scripture reflections, prayers, and writing prompts, is owned by Rooted or our licensors and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
            </p>

            <h3 className="text-lg font-medium mb-2" style={{ color: 'hsl(250 15% 40%)' }}>5.2 Your Content</h3>
            <p className="mb-4 leading-relaxed">
              You retain ownership of any journal entries or personal content you create within the Service. By using the Service, you grant us a limited license to store and display your content solely to provide the Service to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              6. Acceptable Use
            </h2>
            <p className="mb-4 leading-relaxed">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Share your account credentials with others</li>
              <li>Attempt to access the Service through unauthorized means</li>
              <li>Use the Service for any unlawful purpose</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Copy, redistribute, or republish our content</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              7. Disclaimer of Warranties
            </h2>
            <p className="mb-4 leading-relaxed">
              The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the Service will be uninterrupted, error-free, or secure.
            </p>
            <p className="mb-4 leading-relaxed">
              The devotional content is intended for spiritual reflection and personal growth. It is not a substitute for professional religious, psychological, or medical advice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              8. Limitation of Liability
            </h2>
            <p className="mb-4 leading-relaxed">
              To the fullest extent permitted by law, Rooted shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              9. Modifications to Service and Terms
            </h2>
            <p className="mb-4 leading-relaxed">
              We reserve the right to modify or discontinue the Service at any time. We may also update these Terms from time to time. Continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              10. Termination
            </h2>
            <p className="mb-4 leading-relaxed">
              We may terminate or suspend your account at any time for violations of these Terms. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              11. Governing Law
            </h2>
            <p className="mb-4 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              12. Contact Information
            </h2>
            <p className="mb-4 leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="mb-4 leading-relaxed">
              <strong>Email:</strong> support@rootedfaithjournal.com
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-8" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-white/70">&copy; 2025 Rooted. A space for contemplation and spiritual growth.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
